import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Monitor, Download, ExternalLink, ShieldCheck, Zap, Bell } from 'lucide-react';
import { useThemeStore } from '../store/theme';
import { useMobileAppDetection } from '../hooks/useMobileAppDetection';
import { mobileAppRedirectService } from '../services/mobileAppRedirect.service';

const mobileAppReleased = false;

interface MobileDesktopRedirectPageProps {
    /** Optional bypass handler if you want to allow power-users to proceed on web anyway */
    onProceedAnyway?: () => void;
}

const MobileDesktopRedirectPage: React.FC<MobileDesktopRedirectPageProps> = ({
    onProceedAnyway,
}) => {
    const { colors, theme } = useThemeStore();
    const { isMobile, isIOS, isAndroid } = useMobileAppDetection();
    const [isRedirecting, setIsRedirecting] = useState(false);

    const handleOpenApp = async () => {
        setIsRedirecting(true);

        try {
            await mobileAppRedirectService.redirectToMobileApp(
                isIOS,
                isAndroid,
                {
                    fallbackDelay: 2000,
                    showFallback: true,
                    onAppOpen: () => setIsRedirecting(false),
                    onFallback: () => setIsRedirecting(false),
                }
            );
        } catch (error) {
            console.error('Failed to redirect to mobile app:', error);
            setIsRedirecting(false);
        }
    };

    const handleDownloadApp = () => {
        const storeUrl = isIOS
            ? 'https://apps.apple.com/us/app/idearify/id6670697772' // App Store URL
            : 'https://play.google.com/store/apps/details?id=com.idearify'; // Play Store URL

        window.open(storeUrl, '_blank');
    };

    // If accessed on desktop/tablet, this component renders nothing (or can host layout children)
    if (!isMobile) {
        return null;
    }

    const isLight = theme === 'light';

    return (
        <div
            className="fixed inset-0 z-50 flex flex-col justify-between p-6 overflow-y-auto"
            style={{
                backgroundColor: isLight ? '#f9fafb' : '#0f172a',
                color: colors.text,
            }}
        >
            {/* Header / Logo */}
            <div className="flex items-center justify-between pt-2">
                <div className="flex items-center space-x-2">
                    <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center shadow-md"
                        style={{ backgroundColor: colors.primary }}
                    >
                        <span className="text-white font-bold text-xl">I</span>
                    </div>
                    <span className="text-xl font-bold tracking-tight" style={{ color: colors.text }}>
                        Idearify
                    </span>
                </div>
            </div>

            {/* Main Hero Card */}
            <div className="my-auto py-8">
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    className="text-center max-w-sm mx-auto"
                >
                    {/* Visual Graphic */}
                    <div className="relative mx-auto w-24 h-24 mb-6 flex items-center justify-center">
                        <div
                            className="absolute inset-0 rounded-full opacity-20 animate-pulse"
                            style={{ backgroundColor: colors.primary }}
                        />
                        <div
                            className="w-20 h-20 rounded-2xl flex items-center justify-center shadow-lg border"
                            style={{
                                backgroundColor: isLight ? '#ffffff' : '#1e293b',
                                borderColor: isLight ? '#e2e8f0' : '#334155',
                            }}
                        >
                            <Monitor size={36} style={{ color: colors.primary }} />
                        </div>
                    </div>

                    {/* Main Copy */}
                    <h1 className="text-2xl font-bold mb-3 leading-tight" style={{ color: colors.text }}>
                        Best experienced on Desktop or Tablet
                    </h1>

                    <p className="text-sm mb-8 leading-relaxed" style={{ color: colors.gray || '#64748b' }}>
                        Our web application is optimized for larger screens to give you full feature access. For mobile devices, please use our native app.
                    </p>

                    {/* Actions */}
                    {!mobileAppReleased ? (
                        <div className="space-y-3">
                            <p className="text-sm mb-8 leading-relaxed" style={{ color: colors.gray || '#64748b' }}>
                                App Coming soon...
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <motion.button
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleOpenApp}
                                disabled={isRedirecting}
                                className="w-full flex items-center justify-center space-x-2 py-3.5 px-5 rounded-xl font-semibold shadow-lg transition-all duration-200 disabled:opacity-50"
                                style={{ backgroundColor: colors.primary, color: '#ffffff' }}
                            >
                                {isRedirecting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        <span>Opening App...</span>
                                    </>
                                ) : (
                                    <>
                                        <ExternalLink size={18} />
                                        <span>Open in Mobile App</span>
                                    </>
                                )}
                            </motion.button>

                            <motion.button
                                whileHover={{ scale: 1.01 }}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleDownloadApp}
                                className="w-full flex items-center justify-center space-x-2 py-3.5 px-5 rounded-xl font-semibold border-2 transition-all duration-200"
                                style={{
                                    backgroundColor: 'transparent',
                                    color: colors.primary,
                                    borderColor: colors.primary,
                                }}
                            >
                                <Download size={18} />
                                <span>Download on {isIOS ? 'App Store' : 'Google Play'}</span>
                            </motion.button>
                        </div>
                    )}
                </motion.div>

                {/* Mobile App Highlights */}
                <div className="mt-10 max-w-sm mx-auto">
                    <div
                        className="p-4 rounded-xl border text-xs space-y-3"
                        style={{
                            backgroundColor: isLight ? 'rgba(255, 255, 255, 0.7)' : 'rgba(30, 41, 59, 0.7)',
                            borderColor: isLight ? '#e2e8f0' : '#334155',
                        }}
                    >
                        <p className="font-semibold text-center uppercase tracking-wider mb-2" style={{ color: colors.gray }}>
                            Why use the mobile app?
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="flex flex-col items-center space-y-1">
                                <Zap size={16} style={{ color: colors.primary }} />
                                <span>Faster performance</span>
                            </div>
                            <div className="flex flex-col items-center space-y-1">
                                <Bell size={16} style={{ color: colors.primary }} />
                                <span>Push alerts</span>
                            </div>
                            <div className="flex flex-col items-center space-y-1">
                                <ShieldCheck size={16} style={{ color: colors.primary }} />
                                <span>Offline sync</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer / Optional Web Override */}
            <div className="text-center pt-4">
                {onProceedAnyway && (
                    <button
                        onClick={onProceedAnyway}
                        className="text-xs underline transition-opacity hover:opacity-80"
                        style={{ color: colors.gray }}
                    >
                        Continue to web version anyway
                    </button>
                )}
            </div>
        </div>
    );
};

export default MobileDesktopRedirectPage;